import { Badge, Button, Checkbox, Heading, Label, Surface, Table, Text } from '@elladx/design-system';
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import AddressForm from '../components/AddressForm';
import PageHeader from '../components/PageHeader';
import DeleteAddressModal from '../components/DeleteAddressModal';
import BulkDeleteModal from '../components/BulkDeleteModal';
import AddressToolbar, { type SortOption, type ShipmentFilter } from '../components/addresses/AddressToolbar';
import BulkActionsBar from '../components/addresses/BulkActionsBar';
import RecipientDetailsModal from '../components/addresses/RecipientDetailsModal';
import Modal from '../components/Modal';
import { copyRecipientAddress } from '../utils/recipientNavigation';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { addressesApi } from '../api/client';
import type { Address, AddressRecord } from '../types';
type AddressPage = Awaited<ReturnType<typeof addressesApi.listPage>>;
import { releaseInFlightKey, tryAcquireInFlightKey } from '../utils/inFlightKeys';
import { requiresBulkQueueDiscard } from '../utils/retryApiIdentity';
import { getOperatorScopedStorageKey } from '../utils/operatorSession';
const emptyAddress: Address = {
    name: '',
    company: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    email: '',
    phone: '',
    country: 'US',
    gift: '',
    tshirt_size: '',
};
function AddressBookPage() {
    const { toasts, removeToast, success, error, warning } = useToast();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<SortOption>('name');
    const [shipmentFilter, setShipmentFilter] = useState<ShipmentFilter>('all');
    const [detailRecipient, setDetailRecipient] = useState<AddressRecord | null>(null);
    const [pendingRecipient, setPendingRecipient] = useState<AddressRecord | null>(null);
    const originalFormRef = useRef<Address>(emptyAddress);
    const addressQueryKey = ['addresses', debouncedSearchQuery, sortBy, shipmentFilter] as const;
    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 300);
        return () => window.clearTimeout(timeout);
    }, [searchQuery]);
    const queryClient = useQueryClient();
    const addressesQuery = useInfiniteQuery({
        queryKey: addressQueryKey,
        queryFn: ({ pageParam, signal }) => addressesApi.listPage({
            limit: 200,
            cursor: pageParam,
            search: debouncedSearchQuery,
            sort: sortBy,
            shipmentFilter,
        }, signal),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    });
    // Flatten the cursor pages into a de-duplicated (by id) list.
    const addresses = useMemo(() => {
        const byId = new Map<string, AddressRecord>();
        for (const page of addressesQuery.data?.pages ?? []) {
            for (const address of page.addresses)
                byId.set(address.id, address);
        }
        return [...byId.values()];
    }, [addressesQuery.data]);
    const loading = addressesQuery.isLoading;
    const loadingMore = addressesQuery.isFetchingNextPage;
    // Optimistically transform every cached address page (create/update/delete)
    // so the list updates without a refetch, matching the previous local-state UX.
    const updateAddressCache = (transform: (addrs: AddressRecord[]) => AddressRecord[]) => {
        queryClient.setQueryData<InfiniteData<AddressPage>>(addressQueryKey, (data) => data ? { ...data, pages: data.pages.map((p) => ({ ...p, addresses: transform(p.addresses) })) } : data);
    };
    // Form state
    const [formAddress, setFormAddress] = useState<Address>(emptyAddress);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const saveInFlightRef = useRef(false);
    const saveGenerationRef = useRef(0);
    const returnFocusRef = useRef<string | null>(null);
    useLayoutEffect(() => {
        if (saving || editingId || showAddForm || !returnFocusRef.current)
            return;
        const target = document.getElementById(returnFocusRef.current)
            ?? document.getElementById('add-address-toggle');
        // Restore only after the collapsed form and enabled trigger are committed.
        // Prevent scrolling away from the new mobile feedback above the list.
        target?.focus({ preventScroll: true });
        if (document.activeElement === target)
            returnFocusRef.current = null;
    }, [saving, editingId, showAddForm, addresses]);
    // Delete modal state
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);
    // Mark shipped state
    const [markingShippedIds, setMarkingShippedIds] = useState<Set<string>>(new Set());
    const markingShippedIdsRef = useRef(new Set<string>());
    // Sync from orders state
    const [syncing, setSyncing] = useState(false);
    const [addressSyncCursor, setAddressSyncCursor] = useState<string | null>(null);
    // Selection state for bulk operations
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    useEffect(() => {
        return () => {
            saveGenerationRef.current += 1;
            saveInFlightRef.current = false;
        };
    }, []);
    // Surface the initial load failure as a toast, once per distinct failure.
    // Uses isLoadingError (first fetch, no data) — not isError — so a refetch
    // failure triggered from a mutation handler doesn't double-toast on top of
    // that handler's own error message.
    useEffect(() => {
        if (addressesQuery.isLoadingError) {
            const message = addressesQuery.error instanceof Error ? addressesQuery.error.message : 'Failed to load addresses';
            error('Load Failed', message);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressesQuery.error, addressesQuery.isLoadingError]);
    useEffect(() => {
        if (addressesQuery.isFetchNextPageError) {
            error('Load More Failed', 'Failed to load more addresses.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressesQuery.isFetchNextPageError]);
    // Surface background refetch failures — e.g. the refresh after a successful
    // create/edit/delete via invalidateQueries — which TanStack otherwise
    // swallows while it keeps rendering the previous data. isRefetchError is
    // false for initial-load failures (isLoadingError above) and for next-page
    // failures (isFetchNextPageError above), so this never double-toasts those.
    useEffect(() => {
        if (addressesQuery.isRefetchError) {
            const message = addressesQuery.error instanceof Error ? addressesQuery.error.message : 'Failed to refresh addresses';
            error('Refresh Failed', `${message}. The list may be out of date.`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressesQuery.isRefetchError]);
    // Reload all pages; returns false if the reload itself failed.
    const reloadAddresses = async (): Promise<boolean> => {
        const result = await addressesQuery.refetch();
        return !result.isError;
    };
    const handleLoadMoreAddresses = async () => {
        if (!addressesQuery.hasNextPage || addressesQuery.isFetchingNextPage)
            return;
        await addressesQuery.fetchNextPage();
    };
    const filteredAddresses = addresses;
    const visibleAddressIds = useMemo(() => new Set(filteredAddresses.map((address) => address.id)), [filteredAddresses]);
    const selectedVisibleAddresses = useMemo(() => filteredAddresses.filter((address) => selectedIds.has(address.id)), [filteredAddresses, selectedIds]);
    // Keep selection scoped to rows the user can currently see and act on.
    useEffect(() => {
        setSelectedIds((previous) => {
            const next = new Set(Array.from(previous).filter((id) => visibleAddressIds.has(id)));
            return next.size === previous.size ? previous : next;
        });
    }, [visibleAddressIds]);
    const handleEdit = (address: AddressRecord) => {
        if (saveInFlightRef.current)
            return;
        // Close add form when editing
        setShowAddForm(false);
        // Toggle accordion - if clicking same address, close it
        if (editingId === address.id) {
            setEditingId(null);
            setFormAddress(emptyAddress);
            return;
        }
        setEditingId(address.id);
        originalFormRef.current = copyRecipientAddress(address);
        setFormAddress(copyRecipientAddress(address));
    };
    const handleCancel = () => {
        if (saveInFlightRef.current)
            return;
        const previouslyEditingId = editingId;
        const wasAdding = showAddForm;
        returnFocusRef.current = previouslyEditingId
            ? `edit-address-toggle-${previouslyEditingId}`
            : wasAdding ? 'add-address-toggle' : null;
        setEditingId(null);
        setShowAddForm(false);
        setFormAddress(emptyAddress);
    };
    const handleAddNew = () => {
        if (saveInFlightRef.current)
            return;
        setEditingId(null);
        setFormAddress(emptyAddress);
        originalFormRef.current = emptyAddress;
        setShowAddForm(true);
    };
    const navigateToRecipient = (address: AddressRecord) => {
        navigate('/', { state: { recipient: copyRecipientAddress(address), recipientSourceId: address.id } });
    };
    const handleShipToRecipient = (address: AddressRecord) => {
        if (saveInFlightRef.current) return;
        if ((showAddForm || editingId) && JSON.stringify(formAddress) !== JSON.stringify(originalFormRef.current)) {
            setPendingRecipient(address);
            return;
        }
        navigateToRecipient(address);
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (saveInFlightRef.current)
            return;
        const submittedEditingId = editingId;
        const submittedAddress = { ...formAddress };
        if (!submittedAddress.name || !submittedAddress.street1 || !submittedAddress.city || !submittedAddress.state || !submittedAddress.zip) {
            error('Validation Error', 'Please fill in all required fields.');
            return;
        }
        if (submittedAddress.gift === 'Tshirt' && !submittedAddress.tshirt_size) {
            error('Validation Error', 'Select a T-shirt size.');
            return;
        }
        saveInFlightRef.current = true;
        const saveGeneration = ++saveGenerationRef.current;
        setSaving(true);
        try {
            if (submittedEditingId) {
                const updated = await addressesApi.update(submittedEditingId, submittedAddress);
                if (saveGeneration !== saveGenerationRef.current)
                    return;
                updateAddressCache((addrs) => addrs.map((addr) => addr.id === submittedEditingId
                    ? {
                        ...updated,
                        shipment_stats: updated.shipment_stats ?? addr.shipment_stats,
                    }
                    : addr));
                void queryClient.invalidateQueries({ queryKey: ['addresses'] });
                success('Address Updated', 'The address has been updated successfully.');
            }
            else {
                await addressesApi.create(submittedAddress);
                if (saveGeneration !== saveGenerationRef.current)
                    return;
                // The server owns membership and sort order for every filtered cache.
                // Refetch the active query and mark inactive filter variants stale.
                void queryClient.invalidateQueries({ queryKey: ['addresses'] });
                success('Address Created', 'The address has been saved successfully.');
            }
            returnFocusRef.current = submittedEditingId
                ? `edit-address-toggle-${submittedEditingId}`
                : 'add-address-toggle';
            setEditingId(null);
            setShowAddForm(false);
            setFormAddress(emptyAddress);
        }
        catch (err) {
            if (saveGeneration !== saveGenerationRef.current)
                return;
            const message = err instanceof Error ? err.message : 'Failed to save address';
            error('Save Failed', message);
        }
        finally {
            if (saveGeneration === saveGenerationRef.current) {
                saveInFlightRef.current = false;
                setSaving(false);
            }
        }
    };
    const handleDelete = async () => {
        if (!deleteId || saveInFlightRef.current)
            return;
        setDeleting(true);
        try {
            await addressesApi.delete(deleteId);
            updateAddressCache((addrs) => addrs.filter((addr) => addr.id !== deleteId));
            void queryClient.invalidateQueries({ queryKey: ['addresses'] });
            setSelectedIds((prev) => {
                const next = new Set(prev);
                next.delete(deleteId);
                return next;
            });
            success('Address Deleted', 'The address has been removed.');
            setDeleteId(null);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete address';
            error('Delete Failed', message);
        }
        finally {
            setDeleting(false);
        }
    };
    const handleMarkShipped = async (addr: AddressRecord) => {
        if (saveInFlightRef.current ||
            !tryAcquireInFlightKey(markingShippedIdsRef.current, addr.id))
            return;
        setMarkingShippedIds(new Set(markingShippedIdsRef.current));
        try {
            await addressesApi.markShipped(addr.id);
            success('Marked as Shipped', `${addr.name} has been marked as shipped.`);
            // Reload addresses to get updated shipment stats
            await reloadAddresses();
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to mark as shipped';
            error('Mark Shipped Failed', message);
        }
        finally {
            releaseInFlightKey(markingShippedIdsRef.current, addr.id);
            setMarkingShippedIds(new Set(markingShippedIdsRef.current));
        }
    };
    const handleSyncFromOrders = async () => {
        setSyncing(true);
        try {
            const result = await addressesApi.syncFromOrders({
                limit: 100,
                cursor: addressSyncCursor || undefined,
            });
            setAddressSyncCursor(result.next_cursor);
            if (result.created > 0) {
                success(result.list_complete ? 'Sync Complete' : 'Sync Page Complete', `Imported ${result.created} address${result.created !== 1 ? 'es' : ''} from this bounded page.`);
                await reloadAddresses();
            }
            else if (!result.list_complete) {
                warning('More History Available', `Processed ${result.processed} records. Select Continue Sync to process the next page.`);
            }
            else {
                success('Sync Complete', 'No new addresses found in shipment history.');
            }
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to sync addresses';
            error('Sync Failed', message);
        }
        finally {
            setSyncing(false);
        }
    };
    const formatDate = (dateStr?: string | null) => {
        if (!dateStr)
            return '-';
        const date = new Date(dateStr);
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
    };
    // Selection handlers
    const handleSelectAll = (checked: boolean) => {
        if (saveInFlightRef.current)
            return;
        if (checked) {
            setSelectedIds(new Set(filteredAddresses.map((addr) => addr.id)));
        }
        else {
            setSelectedIds(new Set());
        }
    };
    const handleSelectOne = (id: string, checked: boolean) => {
        if (saveInFlightRef.current)
            return;
        setSelectedIds((previous) => {
            const next = new Set(previous);
            if (checked && visibleAddressIds.has(id)) {
                next.add(id);
            }
            else {
                next.delete(id);
            }
            return next;
        });
    };
    const clearSelection = () => {
        setSelectedIds(new Set());
    };
    const selectedVisibleCount = selectedVisibleAddresses.length;
    const isAllSelected = filteredAddresses.length > 0 && selectedVisibleCount === filteredAddresses.length;
    const isSomeSelected = selectedVisibleCount > 0;
    // Bulk delete handler
    const handleBulkDelete = async () => {
        if (saveInFlightRef.current)
            return;
        const idsToDelete = selectedVisibleAddresses.map((address) => address.id);
        if (idsToDelete.length === 0)
            return;
        const idSet = new Set(idsToDelete);
        setBulkDeleting(true);
        try {
            const result = await addressesApi.bulkDelete(idsToDelete);
            if (!result.success || result.failed > 0) {
                const refreshed = await reloadAddresses();
                warning('Some Deletes Failed', `${result.deleted} confirmed deleted; ${result.failed} not confirmed.${result.error ? ` ${result.error}.` : ''} ${refreshed ? 'The list has been refreshed.' : 'Reload the page to reconcile the list.'}`);
            }
            else {
                updateAddressCache((addrs) => addrs.filter((addr) => !idSet.has(addr.id)));
                void queryClient.invalidateQueries({ queryKey: ['addresses'] });
                success('Addresses Deleted', `Successfully deleted ${result.deleted} address${result.deleted !== 1 ? 'es' : ''}.`);
            }
            setSelectedIds(new Set());
            setShowBulkDeleteModal(false);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete addresses';
            const refreshed = await reloadAddresses();
            setSelectedIds(new Set());
            setShowBulkDeleteModal(false);
            error('Bulk Delete Failed', `${message}. ${refreshed ? 'The list has been refreshed.' : 'Reload the page to reconcile the list.'}`);
        }
        finally {
            setBulkDeleting(false);
        }
    };
    // Bulk order handler - navigate to bulk ship page with selected addresses
    const handleBulkOrder = () => {
        if (selectedVisibleAddresses.length === 0 || saveInFlightRef.current)
            return;
        // Store selected address IDs in sessionStorage for the bulk ship page
        const selectedAddresses = selectedVisibleAddresses;
        try {
            const storageKey = getOperatorScopedStorageKey('bulkShipAddresses');
            if (!storageKey) {
                throw new Error('Authenticated operator identity is unavailable.');
            }
            const existingQueue = sessionStorage.getItem(storageKey);
            if (requiresBulkQueueDiscard(existingQueue)) {
                warning('Saved Queue Needs Attention', 'A previous bulk queue is preserved. Review or explicitly discard it before starting a new one.');
                navigate('/bulk-ship');
                return;
            }
            sessionStorage.setItem(storageKey, JSON.stringify(selectedAddresses));
            navigate('/bulk-ship');
        }
        catch {
            error('Bulk Queue Unavailable', 'Browser session storage is unavailable. No shipments were started.');
        }
    };
    return (<>
      <ToastContainer toasts={toasts} onClose={removeToast}/>

      <PageHeader title="Recipients" description="Find a saved recipient, check their details, or prepare several shipments" actions={<Button id="add-address-toggle" type="button" aria-expanded={showAddForm} aria-controls={showAddForm ? 'add-address-form-panel' : undefined} onClick={() => (showAddForm ? handleCancel() : handleAddNew())} disabled={saving} variant="primary">
            <Plus size={14}/>
            Add recipient
          </Button>}/>

      {showAddForm && (<Surface className="card" id="add-address-form-panel" style={{ marginBottom: '16px' }}>
          <div className="card-head">
            <Heading className="card-title" as="span" variant="card">New recipient</Heading>
          </div>
          <form onSubmit={handleSubmit} aria-busy={saving}>
            <div className="card-body">
              <AddressForm title="Add New Address" idPrefix="add-addr" address={formAddress} onChange={setFormAddress} disabled={saving} hideHeader/>
            </div>
            <div className="card-foot">
              <Button type="submit" disabled={saving} variant="primary" isLoading={saving}>
                {saving ? 'Saving…' : 'Save address'}
              </Button>
              <Button type="button" onClick={handleCancel} disabled={saving} variant="ghost">
                Cancel
              </Button>
            </div>
          </form>
        </Surface>)}

      {/* Address list card */}
      <Surface className="card">
        <AddressToolbar allSelected={isAllSelected} selectionDisabled={saving} onSelectAll={handleSelectAll} syncing={syncing} syncCursor={addressSyncCursor} onSync={handleSyncFromOrders} searchQuery={searchQuery} onSearchChange={(value) => {
            setSearchQuery(value);
            clearSelection();
        }} shipmentFilter={shipmentFilter} onShipmentFilterChange={(value) => {
            setShipmentFilter(value);
            if (value === 'shipped') setSortBy('recent');
            clearSelection();
        }} sortBy={sortBy} onSortChange={setSortBy}/>

          {isSomeSelected && (<BulkActionsBar count={selectedVisibleCount} disabled={saving} onCreateShipments={handleBulkOrder} onDelete={() => setShowBulkDeleteModal(true)} onClear={clearSelection}/>)}

          {loading ? (<div className="card-body" style={{ display: 'grid', gap: '10px' }}>
              <div className="skeleton" style={{ height: '44px' }}/>
              <div className="skeleton" style={{ height: '44px' }}/>
              <div className="skeleton" style={{ height: '44px' }}/>
            </div>) : addressesQuery.isLoadingError ? (<div className="card-body" role="alert">
              <Heading as="h2" variant="card" style={{ marginBottom: '8px' }}>Recipients unavailable</Heading>
              <Text className="section-description">Check your connection and retry.</Text>
              <Button type="button" onClick={() => addressesQuery.refetch()} disabled={addressesQuery.isFetching} isLoading={addressesQuery.isFetching} variant="secondary">
                Retry
              </Button>
            </div>) : filteredAddresses.length === 0 ? (<div className="empty-state">
              <Text>
                {searchQuery
                ? 'No addresses match your search.'
                : shipmentFilter !== 'all' ? 'No recipients match this filter.' : 'No saved addresses yet. Add one above!'}
              </Text>
              {(searchQuery || shipmentFilter !== 'all') && <Button type="button" variant="secondary" onClick={() => { setSearchQuery(''); setDebouncedSearchQuery(''); setShipmentFilter('all'); clearSelection(); }}>Clear search and filters</Button>}
            </div>) : (<div className="table-wrapper">
              <Table className="recipient-table">
                <thead>
                  <tr>
                    <th scope="col"><span className="visually-hidden">Selection</span></th>
                    <th>Name</th>
                    <th>Address</th>
                    <th>City, State</th>
                    <th>Gift</th>
                    <th style={{ textAlign: 'right' }}>Shipments</th>
                    <th>Last Shipped</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAddresses.map((addr) => (<React.Fragment key={addr.id}>
                      <tr className={editingId === addr.id ? "editing-row" : ""}>
                        <td className="recipient-selection">
                          <Label className="recipient-select-target">
                            <Checkbox checked={selectedIds.has(addr.id)} onChange={(e) => handleSelectOne(addr.id, e.target.checked)} aria-label={`Select address for ${addr.name || 'unnamed recipient'}`} disabled={saving} style={{ cursor: 'pointer' }}/>
                          </Label>
                        </td>
                        <td data-label="Name">
                          <strong>{addr.name}</strong>
                          {addr.company && (<div style={{ color: 'var(--dx-color-muted)' }}>
                              {addr.company}
                            </div>)}
                        </td>
                        <td data-label="Address">
                          {addr.street1}
                          {addr.street2 && `, ${addr.street2}`}
                        </td>
                        <td data-label="City, State">
                          {addr.city}, {addr.state} {addr.zip}
                          {addr.country && addr.country !== 'US' ? ` ${addr.country}` : ''}
                        </td>
                        <td data-label="Gift">
                          {addr.gift
                    ? addr.gift === 'Tshirt' && addr.tshirt_size
                        ? `${addr.gift} (${addr.tshirt_size})`
                        : addr.gift
                    : '-'}
                        </td>
                        <td data-label="Shipments" style={{ textAlign: 'right' }}>
                          <Badge className="count-pill">
                            {addr.shipment_stats?.count || 0}
                          </Badge>
                        </td>
                        <td data-label="Last Shipped" className="td-mono td-muted">
                          {formatDate(addr.shipment_stats?.last_shipped_at)}
                        </td>
                        <td data-label="Actions" style={{ textAlign: 'right' }}>
                          <div className="row-actions">
                            <Button type="button" variant="primary" size="sm" onClick={() => handleShipToRecipient(addr)} disabled={saving}>Ship to this recipient</Button>
                            <Button type="button" variant="secondary" size="sm" onClick={() => setDetailRecipient(addr)} disabled={saving}>View recipient</Button>
                            {(addr.shipment_stats?.count || 0) === 0 && (<Button onClick={() => handleMarkShipped(addr)} disabled={saving || markingShippedIds.has(addr.id)} title="Mark as shipped" variant="secondary" type="submit" size="sm" isLoading={markingShippedIds.has(addr.id)}>
                                {markingShippedIds.has(addr.id) ? 'Marking…' : 'Mark shipped'}
                              </Button>)}
                            <Button id={`edit-address-toggle-${addr.id}`} onClick={() => handleEdit(addr)} disabled={saving} aria-expanded={editingId === addr.id} aria-controls={`edit-address-panel-${addr.id}`} title={editingId === addr.id ? 'Close edit' : 'Edit address'} variant={editingId === addr.id ? "primary" : "secondary"} type="submit" size="sm">
                              {editingId === addr.id ? 'Close' : 'Edit'}
                            </Button>
                            <Button onClick={() => setDeleteId(addr.id)} disabled={saving} title="Delete address" aria-label="Delete address" variant="danger-secondary" type="submit" size="sm">
                              <Trash2 size={12}/>
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {/* Accordion Edit Row */}
                      <tr className={`accordion-edit-row ${editingId === addr.id ? "open" : ""}`}>
                        <td colSpan={8} style={{ padding: 0, border: 'none' }}>
                          <div id={`edit-address-panel-${addr.id}`} className={`accordion-edit-content ${editingId === addr.id ? "open" : ""}`} aria-hidden={editingId !== addr.id}>
                            <form onSubmit={handleSubmit} style={{ padding: '16px' }} aria-busy={saving}>
                              <div style={{ fontWeight: 600, color: 'var(--dx-color-text)', marginBottom: '14px' }}>
                                Edit address
                              </div>
                              <AddressForm title="Edit Address" idPrefix={`edit-addr-${addr.id}`} address={formAddress} onChange={setFormAddress} disabled={saving} hideHeader/>

                              <div className="action-buttons" style={{ marginTop: '16px' }}>
                                <Button type="submit" disabled={saving} variant="primary" isLoading={saving}>
                                  {saving ? 'Saving…' : 'Update address'}
                                </Button>
                                <Button type="button" onClick={handleCancel} disabled={saving} variant="ghost">
                                  Cancel
                                </Button>
                              </div>
                            </form>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>))}
                </tbody>
              </Table>
            </div>)}
          {!addressesQuery.isLoadingError && (<div className="card-foot-bar">
            <span className="count">
              {loading
            ? ' '
            : `${filteredAddresses.length} address${filteredAddresses.length !== 1 ? 'es' : ''}`}
            </span>
            {addressesQuery.hasNextPage && !loading && (<Button type="button" onClick={handleLoadMoreAddresses} disabled={loadingMore} variant="secondary" size="sm" isLoading={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>)}
          </div>)}
      </Surface>

      <RecipientDetailsModal recipient={detailRecipient} onClose={() => setDetailRecipient(null)} onShip={handleShipToRecipient} onEdit={recipient => { setDetailRecipient(null); if (editingId !== recipient.id) handleEdit(recipient); }} onDelete={recipient => { setDetailRecipient(null); setDeleteId(recipient.id); }}/>
      <Modal isOpen={Boolean(pendingRecipient)} title="Discard unsaved recipient changes?" onClose={() => setPendingRecipient(null)}>
        <Text>Your unsaved recipient edits will be discarded if you continue to a new shipment.</Text>
        <div className="management-actions"><Button type="button" variant="primary" onClick={() => setPendingRecipient(null)}>Keep editing</Button><Button type="button" variant="danger-secondary" onClick={() => { if (pendingRecipient) navigateToRecipient(pendingRecipient); }}>Discard and continue</Button></div>
      </Modal>

      <DeleteAddressModal isOpen={!!deleteId} deleting={deleting} onClose={() => setDeleteId(null)} onConfirm={handleDelete}/>

      <BulkDeleteModal isOpen={showBulkDeleteModal} count={selectedVisibleCount} deleting={bulkDeleting} onClose={() => setShowBulkDeleteModal(false)} onConfirm={handleBulkDelete}/>
    </>);
}
export default AddressBookPage;
